import * as React from "react"
import { ChevronDown, Check } from "lucide-react"
import { cn } from "@/utils"

const Select = ({ children, value, onValueChange, ...props }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [selectedValue, setSelectedValue] = React.useState(value || "")
  const selectRef = React.useRef(null)
  
  React.useEffect(() => {
    setSelectedValue(value || "")
  }, [value])

  // Fechar ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  const handleSelect = (newValue) => {
    setSelectedValue(newValue)
    onValueChange?.(newValue)
    setIsOpen(false)
  }

  return (
    <div ref={selectRef} className="relative" {...props}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          // Passar props diferentes dependendo do tipo de componente
          const childProps = {
            isOpen,
            setIsOpen,
            selectedValue,
            onSelect: handleSelect
          };
          
          return React.cloneElement(child, childProps);
        }
        return child
      })}
    </div>
  )
}

const SelectTrigger = React.forwardRef(({ className, children, isOpen, setIsOpen, selectedValue, onSelect, ...props }, ref) => {
  // Passar selectedValue para os filhos (SelectValue)
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child) && child.type === SelectValue) {
      return React.cloneElement(child, { selectedValue });
    }
    return child;
  });

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      onClick={() => setIsOpen?.(!isOpen)}
      {...props}
    >
      {childrenWithProps}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
})
SelectTrigger.displayName = "SelectTrigger"

const SelectValue = ({ placeholder, selectedValue }) => {
  return (
    <span className={cn(selectedValue ? "text-foreground" : "text-muted-foreground")}>
      {selectedValue || placeholder}
    </span>
  )
}

const SelectContent = ({ className, children, isOpen, onSelect, setIsOpen, selectedValue, ...props }) => {
  if (!isOpen) return null

  // Contar número de itens para determinar se precisa de scroll
  const itemCount = React.Children.count(children);
  const needsScroll = itemCount > 10;

  return (
    <div
      className={cn(
        "absolute top-full left-0 z-50 mt-1 w-full min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95",
        className
      )}
      {...props}
    >
      <div className={cn(
        "p-1",
        needsScroll && "max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      )}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { onSelect })
          }
          return child
        })}
      </div>
    </div>
  )
}

const SelectItem = React.forwardRef(({ className, children, value, onSelect, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    onClick={() => onSelect?.(value)}
    {...props}
  >
    {children}
  </div>
))
SelectItem.displayName = "SelectItem"

export {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
}
